import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Store, FileText, CreditCard, Printer, MessageCircle, SlidersHorizontal } from "lucide-react";
import { Button, PageHeader } from "@/components/ui-kit";
import StoreTab from "./settings/StoreTab";
import FiscalTab from "./settings/FiscalTab";
import PaymentTab from "./settings/PaymentTab";
import PrintAgentTab from "./settings/PrintAgentTab";
import WhatsAppTab from "./settings/WhatsAppTab";

const NUMERIC = ["free_shipping_min", "stock_min_alert", "commission_default", "nfe_serie", "nfce_serie"];
const READONLY = ["cert_uploaded", "cert_filename", "cert_uploaded_at", "focusnfe_token_set", "fiscal_ready", "pagarme_ready", "pagarme_key_set", "pagarme_key_from_env", "card_enabled", "pg_recipient_data"];

const TABS = [
  ["loja", "Dados da loja", Store],
  ["fiscal", "Emissão & Certificado", FileText],
  ["pagamento", "Pagamento", CreditCard],
  ["impressao", "Agente de impressão", Printer],
  ["whatsapp", "WhatsApp", MessageCircle],
];

export default function Settings() {
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("loja");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/settings").then((r) => setForm(r.data));
  useEffect(() => { load(); }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      NUMERIC.forEach((k) => { payload[k] = parseFloat(payload[k]) || 0; });
      READONLY.forEach((k) => delete payload[k]);
      await api.put("/admin/settings", payload);
      toast.success("Configurações salvas");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <p className="text-slate-500">Carregando...</p>;

  const showSave = tab === "loja" || tab === "fiscal";

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-settings-page">
      <PageHeader
        eyebrow="Sistema"
        title="Configurações"
        icon={SlidersHorizontal}
        description="Dados da loja, emissão fiscal, pagamento e o agente de impressão do balcão."
      />

      {/* Tabs. `overflow-x-auto` no celular: as quatro abas somam mais que 390px
          e, sem isto, "Agente de impressão" quebrava para uma segunda fileira
          desalinhada da borda inferior — a linha do tab ativo deixava de fazer
          sentido como trilho. */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto scrollbar-thin" data-testid="settings-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`settings-tab-${id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
              tab === id ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon size={16} aria-hidden /> {label}
          </button>
        ))}
      </div>

      {/* Conteúdo do tab */}
      {tab === "loja" && <StoreTab form={form} setField={setField} />}
      {tab === "fiscal" && <FiscalTab form={form} setField={setField} reload={load} />}
      {tab === "pagamento" && <PaymentTab form={form} reload={load} />}
      {tab === "impressao" && <PrintAgentTab />}
      {tab === "whatsapp" && <WhatsAppTab />}

      {showSave && (
        // `Button` do kit no lugar do botão à mão: era o único "salvar" do
        // painel sem estado de carregando e sem o anel de foco do sistema.
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Button size="lg" loading={saving} onClick={save} data-testid="settings-save-btn" className="sm:w-auto">
            {saving ? "Salvando..." : "Salvar configurações"}
          </Button>
          <span className="text-xs text-slate-400">Salva os dados da loja e os parâmetros fiscais.</span>
        </div>
      )}
    </div>
  );
}
